import type {
  Alert,
  Asset,
  DependencyEdge,
  IntelReport,
  KGEdge,
  KGEntity,
  ThreatActor,
  Vessel,
  Vulnerability,
} from '@/types';

/**
 * In-memory knowledge graph built from the loaded data sources. Provides
 * primitive queries (find by attribute, neighbours, paths) plus a small
 * pattern-matching natural-language adapter.
 */
export class KnowledgeGraph {
  private entitiesById = new Map<string, KGEntity>();
  private edges: KGEdge[] = [];
  private adj = new Map<string, KGEdge[]>();

  addEntity(e: KGEntity) {
    this.entitiesById.set(e.entity_id, e);
  }

  addEdge(e: KGEdge) {
    this.edges.push(e);
    if (!this.adj.has(e.from)) this.adj.set(e.from, []);
    this.adj.get(e.from)!.push(e);
  }

  get(id: string): KGEntity | undefined {
    return this.entitiesById.get(id);
  }

  all(): KGEntity[] {
    return Array.from(this.entitiesById.values());
  }

  neighbours(id: string): { entity: KGEntity; edge: KGEdge }[] {
    const out: { entity: KGEntity; edge: KGEdge }[] = [];
    for (const e of this.adj.get(id) ?? []) {
      const target = this.entitiesById.get(e.to);
      if (target) out.push({ entity: target, edge: e });
    }
    for (const e of this.edges) {
      if (e.to === id && e.from !== id) {
        const source = this.entitiesById.get(e.from);
        if (source) out.push({ entity: source, edge: e });
      }
    }
    return out;
  }

  /** BFS shortest path of entities. */
  path(fromId: string, toId: string, maxDepth = 5): KGEntity[] {
    if (fromId === toId) {
      const e = this.entitiesById.get(fromId);
      return e ? [e] : [];
    }
    const queue: string[][] = [[fromId]];
    const visited = new Set<string>([fromId]);
    while (queue.length) {
      const path = queue.shift()!;
      const last = path[path.length - 1]!;
      if (path.length > maxDepth) continue;
      for (const e of this.adj.get(last) ?? []) {
        if (visited.has(e.to)) continue;
        const next = [...path, e.to];
        if (e.to === toId) {
          return next.map((id) => this.entitiesById.get(id)!).filter(Boolean);
        }
        visited.add(e.to);
        queue.push(next);
      }
    }
    return [];
  }

  search(text: string): KGEntity[] {
    const q = text.toLowerCase();
    return this.all().filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        (e.aliases ?? []).some((a) => a.toLowerCase().includes(q)) ||
        e.entity_id.toLowerCase().includes(q),
    );
  }

  /**
   * Natural-language-ish query. Recognises a small set of patterns that map
   * onto graph operations. Anything unrecognised falls back to a free-text
   * search across entity names.
   */
  query(text: string): KGQueryResult {
    const q = text.trim().toLowerCase();

    // pattern: "vessels near <asset>"
    const m1 = q.match(/vessels?\s+(?:near|approaching|around)\s+(.+)/);
    if (m1) {
      const target = m1[1]!.trim();
      const asset = this.findEntity(target, 'asset');
      if (!asset)
        return {
          summary: `No asset matching "${target}" found.`,
          entities: [],
          edges: [],
        };
      return this.entitiesByKindNear(asset.entity_id, 'vessel');
    }

    // pattern: "alerts for <asset>"
    const m2 = q.match(/(?:alerts|incidents|events)\s+(?:for|on|at)\s+(.+)/);
    if (m2) {
      const target = m2[1]!.trim();
      const asset = this.findEntity(target, 'asset');
      if (!asset)
        return { summary: `No asset matching "${target}" found.`, entities: [], edges: [] };
      const events = this.neighbours(asset.entity_id)
        .filter((n) => n.entity.kind === 'event')
        .map((n) => n.entity);
      return {
        summary: `${events.length} alerts linked to ${asset.name}.`,
        entities: [asset, ...events],
        edges: [],
      };
    }

    // pattern: "<actor> targets" / "what does <actor> target"
    const m3 = q.match(/(?:^|.*\s)(sandworm|chernovite|xenotime|wirte|fin12)/i);
    if (m3) {
      const actorAlias = m3[1]!;
      const actor = this.all().find(
        (e) =>
          e.kind === 'threat_actor' &&
          (e.name.toLowerCase().includes(actorAlias.toLowerCase()) ||
            (e.aliases ?? []).some((a) =>
              a.toLowerCase().includes(actorAlias.toLowerCase()),
            )),
      );
      if (!actor)
        return { summary: `No threat actor matching "${actorAlias}".`, entities: [], edges: [] };
      return this.entitiesByKindNear(actor.entity_id, 'asset');
    }

    // pattern: "path between X and Y"
    const m4 = q.match(/(?:path|route|connection)\s+(?:between|from)\s+(.+?)\s+(?:and|to)\s+(.+)/);
    if (m4) {
      const a = this.findEntity(m4[1]!, undefined);
      const b = this.findEntity(m4[2]!, undefined);
      if (!a || !b) return { summary: 'Could not resolve both endpoints.', entities: [], edges: [] };
      const path = this.path(a.entity_id, b.entity_id);
      return {
        summary: path.length
          ? `Found ${path.length}-hop path.`
          : 'No path within depth 5.',
        entities: path,
        edges: [],
      };
    }

    // pattern: "assets in <country>"
    const m5 = q.match(/assets?\s+(?:in|at|across)\s+([a-z]{2,})/);
    if (m5) {
      const code = m5[1]!.toUpperCase();
      const list = this.all().filter(
        (e) => e.kind === 'asset' && (e.attributes['country'] ?? '') === code,
      );
      return {
        summary: `${list.length} assets in ${code}.`,
        entities: list,
        edges: [],
      };
    }

    // Fallback: free-text search
    const hits = this.search(text);
    return {
      summary: `${hits.length} results for "${text}".`,
      entities: hits.slice(0, 50),
      edges: [],
    };
  }

  private entitiesByKindNear(
    centreId: string,
    kind: KGEntity['kind'],
  ): KGQueryResult {
    const centre = this.get(centreId);
    if (!centre) return { summary: 'Centre not found.', entities: [], edges: [] };
    const matched = this.neighbours(centreId)
      .filter((n) => n.entity.kind === kind)
      .map((n) => n.entity);
    return {
      summary: `${matched.length} ${kind} near ${centre.name}.`,
      entities: [centre, ...matched],
      edges: [],
    };
  }

  private findEntity(text: string, kind?: KGEntity['kind']): KGEntity | undefined {
    const q = text.toLowerCase();
    return this.all().find(
      (e) =>
        (!kind || e.kind === kind) &&
        (e.name.toLowerCase().includes(q) ||
          e.entity_id.toLowerCase().includes(q) ||
          (e.aliases ?? []).some((a) => a.toLowerCase().includes(q))),
    );
  }
}

export interface KGQueryResult {
  summary: string;
  entities: KGEntity[];
  edges: KGEdge[];
}

/**
 * Build the knowledge graph from all available data.
 */
export function buildKnowledgeGraph(args: {
  assets: Asset[];
  alerts: Alert[];
  vessels: Vessel[];
  reports: IntelReport[];
  actors: ThreatActor[];
  vulnerabilities: Vulnerability[];
  edges: DependencyEdge[];
}): KnowledgeGraph {
  const kg = new KnowledgeGraph();

  for (const a of args.assets) {
    kg.addEntity({
      entity_id: a.asset_id,
      kind: 'asset',
      name: a.name,
      aliases: [a.asset_id, a.owner_operator],
      attributes: {
        country: a.country,
        sector: a.sector,
        type: a.type,
        criticality: a.criticality_rating,
      },
      confidence: 100,
      sources: a.data_sources,
    });
  }

  for (const e of args.edges) {
    kg.addEdge({
      edge_id: e.edge_id,
      from: e.from_asset_id,
      to: e.to_asset_id,
      kind: 'associated_with',
      weight: 1 - (e.has_backup ? 0.3 : 0),
      observed_at: new Date().toISOString(),
      confidence: e.confidence,
    });
  }

  for (const v of args.vessels) {
    const id = `VESS-${v.mmsi}`;
    kg.addEntity({
      entity_id: id,
      kind: 'vessel',
      name: v.name,
      aliases: [v.mmsi, v.imo ?? ''],
      attributes: { flag: v.flag, type: v.type, ais_status: v.ais_status },
      confidence: v.ais_status === 'normal' ? 95 : 65,
      sources: ['AIS'],
      last_observed: v.last_seen,
    });
    if (v.proximity_alert) {
      kg.addEdge({
        edge_id: `EDG-VESS-${v.mmsi}-${v.proximity_alert.asset_id}`,
        from: id,
        to: v.proximity_alert.asset_id,
        kind: 'visited',
        weight: 0.9,
        observed_at: v.last_seen,
        confidence: 80,
      });
    }
  }

  for (const a of args.alerts) {
    const id = `EVT-${a.alert_id}`;
    kg.addEntity({
      entity_id: id,
      kind: 'event',
      name: a.title,
      attributes: {
        severity: a.severity,
        category: a.category,
        status: a.status,
      },
      confidence: a.confidence,
      sources: ['Iris Detection'],
      first_observed: a.detected_at,
    });
    for (const aid of a.asset_ids) {
      kg.addEdge({
        edge_id: `EDG-${id}-${aid}`,
        from: id,
        to: aid,
        kind: 'detected_at',
        weight: 1.0,
        observed_at: a.detected_at,
        confidence: a.confidence,
      });
    }
  }

  for (const r of args.reports) {
    kg.addEntity({
      entity_id: r.report_id,
      kind: 'document',
      name: r.title,
      attributes: { source: r.source, classification: r.classification },
      confidence: r.confidence.composite,
      sources: [r.source],
      first_observed: r.timestamp,
    });
    for (const id of r.related_asset_ids) {
      kg.addEdge({
        edge_id: `EDG-${r.report_id}-${id}`,
        from: r.report_id,
        to: id,
        kind: 'reported_in',
        weight: 0.8,
        observed_at: r.timestamp,
        confidence: r.confidence.composite,
      });
    }
  }

  for (const t of args.actors) {
    kg.addEntity({
      entity_id: t.actor_id,
      kind: 'threat_actor',
      name: t.name,
      aliases: t.aliases,
      attributes: {
        attribution: t.attribution,
        threat_level: t.threat_level,
      },
      confidence: 90,
      sources: ['Threat Intelligence'],
    });
    for (const sector of t.target_sectors) {
      const sectorAssets = args.assets.filter((a) => a.sector === sector);
      for (const asset of sectorAssets) {
        if (asset.country !== 'XX' && t.target_regions.length > 0) {
          kg.addEdge({
            edge_id: `EDG-${t.actor_id}-${asset.asset_id}`,
            from: t.actor_id,
            to: asset.asset_id,
            kind: 'attacked',
            weight: 0.4,
            observed_at: t.active_since,
            confidence: 60,
          });
        }
      }
    }
  }

  return kg;
}
